import React, { useState, type ReactNode } from 'react';
import Image from 'next/image';
import { Box, type SxProps, type Theme } from '@mui/material';

export interface ThumbnailProps {
  /** Image URL. When empty/nullish or it fails to load, `fallback`/`fallbackSrc` is used. */
  src?: string | null;
  /** Accessible alt text (empty by default — decorative). */
  alt?: string;
  /** Square edge length (any CSS length), e.g. '4rem' or 64. */
  size?: number | string;
  /** Corner radius (any CSS length). */
  radius?: number | string;
  /** Node rendered (filling the box) when `src` is empty or fails to load. Takes precedence over `fallbackSrc`. */
  fallback?: ReactNode;
  /** Image shown when `src` is empty or fails to load (used only when `fallback` is not given). */
  fallbackSrc?: string;
  /** `next/image` sizes hint. Defaults to '64px'. */
  sizes?: string;
  /** Bypass the Next.js optimizer (small static assets). Defaults to false. */
  unoptimized?: boolean;
  /** MUI sx overrides for the container (merged last). */
  sx?: SxProps<Theme>;
  /** Test ID (on the container). */
  testId?: string;
}

/**
 * Thumbnail
 *
 * Atomic level: Atom.
 *
 * A fixed-size, rounded, cover-cropped `next/image` with a graceful fallback for
 * missing or broken sources. Zero domain knowledge. Uses `fill` inside a sized,
 * clipped container so it works with any CSS length for `size`.
 */
export function Thumbnail({
  src,
  alt = '',
  size = '4rem',
  radius = '12px',
  fallback,
  fallbackSrc,
  sizes = '64px',
  unoptimized = false,
  sx,
  testId,
}: ThumbnailProps) {
  const [errored, setErrored] = useState(false);

  // Reset the error state during render if the source changes (React's
  // "adjust state on prop change" pattern — avoids a set-state-in-effect).
  const [trackedSrc, setTrackedSrc] = useState(src);
  if (src !== trackedSrc) {
    setTrackedSrc(src);
    setErrored(false);
  }

  const missing = !src || errored;
  const useFallbackNode = missing && fallback != null;
  const resolved = missing ? fallbackSrc : src;

  return (
    <Box
      data-testid={testId}
      sx={{
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: radius,
        overflow: 'hidden',
        ...sx,
      }}
    >
      {useFallbackNode ? (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {fallback}
        </Box>
      ) : (
        resolved && (
          <Image
            src={resolved}
            alt={alt}
            fill
            sizes={sizes}
            unoptimized={unoptimized}
            onError={() => {
              if (fallbackSrc && resolved !== fallbackSrc) {
                setErrored(true);
              }
            }}
            style={{ objectFit: 'cover' }}
          />
        )
      )}
    </Box>
  );
}

export default Thumbnail;
